// HOOKS
import useColumns from "../../hooks/other/useColumns.other.hook";
import useSelectAll from "../../hooks/other/useSelectAll.other.hook";
import { useContext, useEffect } from "react";
// CONTEXTS
import { ProductsContext } from "../../contexts/products/products.context";
// COMPONENTS
import QueryContainer from "../../components/tables/query/queryContainer.table.component";
import PaginationBar from "../../components/tables/customizableTable/features/pagination/paginationBar";
import Refresh from "../../components/refresh/refresh.component";
import Spinner from "../../components/spinner/spinner.component";
import { productColumns } from "../../assets/variables/columns/product.columns";
import Table from "../../components/tables/customizableTable/table.customizableTable";
import GetProductFormProps from "../../components/formProps/products/product.formProp";
import ProductActionBar from "../../components/actionBar/products/products.actionBar";
import ProductInfoBar from "../../components/infoBar/products/product.infoBar";

// CONSTANTS
const CLASSNAMES = {
  pagination: {
    arrow:
      "active:shadow-inner active:shadow-black bg-lightPrimary hover:bg-lightSecondary shadow shadow-black hover:cursor-pointer hover:dark:bg-darkSecondary dark:bg-darkPrimary dark:fill-white rounded-md w-8 h-8",
    currentPage:
      "shadow-inner shadow-black dark:bg-darkSecondary bg-lightSecondary dark:text-white text-white hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
    page: "active:shadow-inner active:shadow-black shadow shadow-black hover:dark:bg-darkSecondary hover:bg-lightSecondary active:shadow-inner dark:text-white text-white dark:bg-darkPrimary bg-lightPrimary hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
  },
  columnFilter: {
    dropdownButton: {
      dropdown:
        "border-animate rounded h-[250px] w-[250px] overflow-y-scroll absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  limit: {
    dropdownButton: {
      dropdown:
        "border-animate rounded absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  spinner: {
    container: "flex w-full h-full justify-center items-center",
    spinner:
      "shadow animate-spin border-dashed rounded-full h-16 w-16 border-2 border-primary",
  },
  statusFilter: {
    dropdownButton: {
      dropdown:
        "w-[225px] border-animate rounded overflow-y-auto absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
};

const ProductsPage = () => {
  const { selectAll, handleSelectAll, resetSelectAll } = useSelectAll();
  const { columns, setColumns, handleColumnFilterCheck, handleResize } =
    useColumns(productColumns); // Set default columns
  const {
    products,
    metaData,
    getProducts,
    isLoading,
    query,
    handleChange,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
    selected,
    setSelected,
    removeSelected,
    resetSelected,
    addSelected,
  } = useContext(ProductsContext);
  const { limit, page, search, sort } = query;

  // Fetch products on page load
  useEffect(() => {
    getProducts();
  }, []);
  // Reset selectAll when selected changes
  useEffect(() => {
    if (selected.length === 0) resetSelectAll();
  }, [selected]);
  // Reset selectAll when page changes
  useEffect(() => {
    resetSelectAll();
  }, [page]);
  // Add/Remove products from selected
  const selectAllHandler = () => {
    const updatedSelectedProducts = handleSelectAll(selected, products, "_id");
    setSelected(updatedSelectedProducts);
  };
  const handleSelect = (event) => {
    const { _id } = event.target.dataset;
    const existingSelection = selected?.find((item) => item._id === _id);
    existingSelection
      ? removeSelected(_id)
      : addSelected(products.find((item) => item._id === _id));
  };

  const changeHandlers = {
    selectAllHandler,
    handleSelect,
    handleChange: handleChange,
    handleMultiSelectChange: () => {},
  };
  return (
    <div
      style={{ height: "calc(100vh - 136px)" }}
      className="p-4 flex flex-col gap-2 overflow-y-hidden"
    >
      <h1 className="font-ubuntuBold text-lg items-center text-center">
        Products
      </h1>
      <div className="flex flex-row justify-between">
        <QueryContainer
          queryProps={{
            search: {
              handleChange: handleSearch,
            },
            limit: {
              handleChange: handleLimitChange,
              limit: limit,
            },
            columnFilter: {
              handleChange: handleColumnFilterCheck,
              columns: columns,
            },
          }}
          classNames={{
            limit: CLASSNAMES.limit,
            columnFilter: CLASSNAMES.columnFilter,
          }}
        ></QueryContainer>
        <ProductActionBar />
      </div>
      <div className="p-2">
        <PaginationBar
          metaData={metaData}
          currentPage={page}
          totalPages={metaData?.totalPages || 1}
          handleChange={handlePageChange}
          classNames={CLASSNAMES.pagination}
        />
      </div>
      <div className="flex flex-row justify-between">
        <ProductInfoBar
          metaData={metaData}
          selectedTagProps={{
            label: `Products: ${selected.length || 0}`,
            handleClear: resetSelected,
          }}
        />
        <Refresh minimumValue={30} handleEvent={getProducts} />
      </div>
      {isLoading || !products?.length > 0 ? (
        <div className={CLASSNAMES.spinner.container}>
          <Spinner classNames={CLASSNAMES.spinner} />
        </div>
      ) : (
        <div className="flex items-center justify-start overflow-hidden w-full h-full">
          <div className="overflow-x-auto h-full px-1 pb-8">
            <Table
              data={products}
              columnProps={{ columns, handleResize }}
              changeHandlers={changeHandlers}
              select={true}
              selectAll={selectAll}
              selectedItems={selected}
              metaData={metaData}
              sortProps={{ sort, handleSort }}
              getFormProps={GetProductFormProps}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
